import { Spinner } from "@nextui-org/react";
import { useState, useEffect, useRef } from "react";
import { addNotification } from "../../../../shared/states/notification";

interface RefineTextModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedText: string;
    onRefine: (refinedText: string) => void;
    onRefineRequest?: (instructionsForChange: string) => Promise<void>;
    position?: { top: number; left: number } | null;
  }
  
  const RefineTextModal: React.FC<RefineTextModalProps> = ({ 
    isOpen, 
    onClose, 
    selectedText, 
    onRefine,
    onRefineRequest,
    position 
  }) => {
    const [refinePrompt, setRefinePrompt] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [adjustedPosition, setAdjustedPosition] = useState<{ top: number; left: number; transform: string } | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Smart positioning logic - simplified for absolute positioning
    useEffect(() => {
      if (!position || !isOpen) {
        setAdjustedPosition(null);
        return;
      }

      const calculateSmartPosition = () => {
        const modalWidth = 400; // Fixed width from the modal
        const modalHeight = 60; // approximate height
        const margin = 16; // safety margin

        let { top, left } = position;
        let transform = 'translateY(-100%)'; // Default: above the selection

        // Simple boundary detection - keep it within reasonable bounds
        // Horizontal: ensure modal doesn't go off-screen
        if (left < margin) {
          left = margin;
        }

        // Vertical: if too close to top, place below instead
        if (top < modalHeight + margin) {
          top = top + 20; // Place below the selection
          transform = 'translateY(0%)';
        }

        

        setAdjustedPosition({ top, left, transform });
      };

      // Calculate position immediately
      calculateSmartPosition();

      // Recalculate on window resize and container scroll  
      const handleResize = () => calculateSmartPosition();
      const handleScroll = () => calculateSmartPosition();
      
      const container = document.querySelector('.flex-grow.overflow-auto') as HTMLElement;
      
      window.addEventListener('resize', handleResize);
      if (container) {
        container.addEventListener('scroll', handleScroll);
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        if (container) {
          container.removeEventListener('scroll', handleScroll);
        }
      };
    }, [position, isOpen]);

    // Auto-resize textarea based on content
    useEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const adjustHeight = () => {
        // Reset height to auto to get the actual scrollHeight
        textarea.style.height = 'auto';
        
        const scrollHeight = textarea.scrollHeight;
        const minHeight = 44; // 44px as defined in CSS
        const maxHeight = 132; // 132px as defined in CSS (4-5 lines)
        
        // Set height to content height, but within min/max bounds
        const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
        textarea.style.height = `${newHeight}px`;
      };

      // Adjust height when content changes
      adjustHeight();
    }, [refinePrompt]);

    // Reset text when modal closes
    useEffect(() => {
      if (!isOpen) {
        setRefinePrompt('');
      }
    }, [isOpen]);
  
    const handleRefine = async () => {
      if (!refinePrompt.trim()) return;
      
      setIsProcessing(true);
      try {
        if (onRefineRequest) {
          // Use the new socket-based refine request
          await onRefineRequest(refinePrompt);
          setRefinePrompt('');
        } else {
          // Fallback to old behavior
          await new Promise(resolve => setTimeout(resolve, 1000));
          const refinedText = `Refined: ${selectedText}`;
          onRefine(refinedText);
          onClose();
          setRefinePrompt('');
        }
      } catch (error) {
        console.error('Error refining text:', error);
        addNotification({
          type: 'error',
          title: 'Refine failed',
          message: 'Failed to refine the text. Please try again.',
        });
      } finally {
        setIsProcessing(false);
      }
    };
  
    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleRefine();
      }
    };
  
    if (!isOpen) return null;
  
    // Use smart positioning if available, fallback to original logic
    const finalPosition = adjustedPosition || (position ? {
      top: position.top,
      left: position.left,
      transform: 'translateY(-100%)'
    } : null);

    const modalStyle = finalPosition
      ? { 
          top: `${finalPosition.top}px`, 
          left: `${finalPosition.left}px`, 
          transform: finalPosition.transform 
        }
      : {};

    // Use absolute positioning instead of fixed with high z-index
    const containerClasses = finalPosition
      ? "absolute z-[100]"
      : "absolute inset-0 z-[100] flex items-center justify-center";

  
    return (
      <div 
        ref={modalRef}
        className={containerClasses}
        style={modalStyle}
      >
        <div className="refine-modal-container bg-white rounded-lg shadow-lg border border-gray-200" style={{ width: '400px', padding: '4px' }}>
          <div className="relative" style={{ margin: 0 }}>
            <textarea
              ref={textareaRef}
              value={refinePrompt}
              onChange={(e) => setRefinePrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Refine"
              className="w-full pl-3 pr-10 py-2 text-sm border border-none rounded-md focus:outline-none focus:ring-1 focus:ring-gray-100 bg-gray-100 resize-none overflow-y-auto"
              style={{
                minHeight: '44px',
                maxHeight: '132px', // Approximately 4-5 lines (44px base + ~22px per additional line)
                lineHeight: '22px',
                margin: 0,
                display: 'block'
              }}
              rows={1}
              disabled={isProcessing}
            />
            <button
              onClick={handleRefine}
              disabled={!refinePrompt.trim() || isProcessing}
              className={`absolute right-1 top-1 w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                refinePrompt.trim() && !isProcessing 
                  ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
              }`}
            >
              {isProcessing ? (
                <Spinner size="sm" />
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 19V5M5 12L12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  export default RefineTextModal;